import { ASK_USER_TOOL_NAME } from '@terra/shared-dto-chat-types';
import type { ProcessedMessage } from '../utils/message-processing';
import {
  createHandledConfirmationPart,
  getUserConfirmationsFromMessage,
  isExternalToolResultsUserMessage,
} from './chat-message-list-confirmations';
import type {
  ChatRenderItem,
  RenderablePart,
  SubAgentExecutionGroup,
} from './chat-message-list-types';

export function consumeUserMessageForChatRender({
  processedMessage,
  consumedConfirmationKeys,
  pendingSubAgentGroup,
  appendRenderablePart,
  flushSubAgentGroup,
  pushItem,
  clearHitlResume,
}: {
  processedMessage: ProcessedMessage;
  consumedConfirmationKeys: Set<string>;
  pendingSubAgentGroup: SubAgentExecutionGroup | null;
  appendRenderablePart: (item: RenderablePart) => SubAgentExecutionGroup | null;
  flushSubAgentGroup: (args: { messageIndex: number }) => void;
  pushItem: (item: ChatRenderItem) => void;
  clearHitlResume: () => void;
}): void {
  const { index: uiMessageIndex } = processedMessage;
  const userConfirmations = getUserConfirmationsFromMessage(processedMessage);
  const isAskUserAnswer = userConfirmations.some(
    ({ confirmation }) => confirmation.toolName === ASK_USER_TOOL_NAME,
  );
  const shouldRenderUserBubble =
    (processedMessage.hasVisibleContent || processedMessage.isUserStreaming) &&
    !isAskUserAnswer;
  const hasUnconsumedConfirmation = userConfirmations.some(
    ({ confirmation, confirmationIndex }) => {
      const confirmationItem = createHandledConfirmationPart({
        processedMessage,
        confirmation,
        confirmationIndex,
      });

      return !consumedConfirmationKeys.has(confirmationItem.processedPart.key);
    },
  );

  if (userConfirmations.length > 0 && !hasUnconsumedConfirmation) {
    if (shouldRenderUserBubble) {
      flushSubAgentGroup({ messageIndex: uiMessageIndex });
      clearHitlResume();
      pushItem({ kind: 'user-message', processedMessage });
    }

    return;
  }

  if (userConfirmations.length > 0) {
    for (const { confirmation, confirmationIndex } of userConfirmations) {
      const confirmationItem = createHandledConfirmationPart({
        processedMessage,
        confirmation,
        confirmationIndex,
      });

      consumedConfirmationKeys.add(confirmationItem.processedPart.key);
      appendRenderablePart(confirmationItem);
      if (pendingSubAgentGroup) {
        pendingSubAgentGroup.hasAttachedHITLResponse = true;
      }
    }

    flushSubAgentGroup({ messageIndex: uiMessageIndex });
    if (shouldRenderUserBubble) {
      clearHitlResume();
      pushItem({ kind: 'user-message', processedMessage });
    }

    return;
  }

  if (isExternalToolResultsUserMessage(processedMessage)) {
    return;
  }

  flushSubAgentGroup({ messageIndex: uiMessageIndex });
  clearHitlResume();

  if (
    !processedMessage.hasVisibleContent &&
    !processedMessage.isUserStreaming
  ) {
    return;
  }

  pushItem({ kind: 'user-message', processedMessage });
}
